import { Suspense } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { Topbar } from './components/Topbar';
import { Sidebar } from './components/Sidebar';
import { AppBreadcrumbs } from './components/Breadcrumbs';
import { RouteFallback } from '@/components/feedback/RouteFallback';
import { useUiStore } from '@/store/uiStore';
import { useNotificationSocket } from '@/features/notifications/useNotificationSocket';

const EXPANDED_WIDTH = 264;
const COLLAPSED_WIDTH = 72;

/** Authenticated app shell: responsive sidebar + top bar + breadcrumbed content. */
export function AdminLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const mobileOpen = useUiStore((s) => s.sidebarMobileOpen);
  const setMobileOpen = useUiStore((s) => s.setSidebarMobileOpen);

  // Live notification pushes for the whole authenticated shell.
  useNotificationSocket();

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', lg: 'block' },
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            borderRight: (t) => `1px solid ${t.palette.divider}`,
            transition: (t) =>
              t.transitions.create('width', { duration: t.transitions.duration.shorter }),
            overflowX: 'hidden',
          },
        }}
      >
        <Sidebar collapsed={collapsed} />
      </Drawer>

      {/* Mobile temporary drawer */}
      <Drawer
        variant="temporary"
        open={!isDesktop && mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { lg: 'none' },
          '& .MuiDrawer-paper': { width: EXPANDED_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Sidebar
          collapsed={false}
          showCollapseToggle={false}
          onNavigate={() => setMobileOpen(false)}
        />
      </Drawer>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar />
        <Box component="main" sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, minWidth: 0 }}>
          <AppBreadcrumbs />
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
